<template>
  <AppLayout :breadcrumbs="[{ label: 'TRT Program', link: '/trt' }]">
    <div class="layout-header">
      <div class="header-left">
        <SearchBar :modelValue="searchTerm" @update:modelValue="handleSearchChange" placeholder="Search patients..." />
      </div>
      <div class="header-right">
        <!-- Refreshing pulls from the EMR over the extension bridge, so it only
             works inside the Practice Fusion modal. Outside it the button stays
             disabled and explains why on hover. -->
        <Tooltip v-if="!canRefresh" :text="REFRESH_DISABLED_TEXT" position="bottom">
          <button class="button refresh-blocked" disabled>
            <AlertIcon :size="16" class="button-icon warning-icon" />
            Refresh
          </button>
        </Tooltip>
        <button v-else class="button" @click="handleRefresh" :disabled="isLoading">
          <RefreshIcon :size="16" class="button-icon" :class="{ spinning: isLoading }" />
          Refresh
        </button>
        <button class="button primary" @click="openAddPatientModal">
          Add Patient
        </button>
      </div>
    </div>

    <div class="layout-body">
      <div class="trt-board">
        <TRTBoardColumn
          v-for="col in boardColumns"
          :key="col.key"
          :column="col"
          :records="col.records"
          @edit-monitor-date="openMonitorDateModal"
        />
      </div>
    </div>

    <!-- Modals -->
    <transition name="modal-fade" mode="out-in">
      <div class="modal-background close-on-click" v-if="modal.open" @click="handleModalClick">
        <div class="modal-scroll close-on-click">
          <transition name="modal-pop" mode="out-in">
            <AddTRTPatientModal
              v-if="modal.id === 'addPatient'"
              @add="handleAddPatient"
              @continue="handleContinueToEmr"
            />
            <ConnectEmrModal
              v-else-if="modal.id === 'connectEmr'"
              :patient="pendingPatient"
              @connect="handleEmrConnected"
            />
            <SyncTRTModal
              v-else-if="modal.id === 'syncTrt'"
              :patients="patients"
              @done="handleSyncDone"
            />
            <SetMonitorDateModal
              v-else-if="modal.id === 'monitorDate'"
              :patient="monitorPatient"
              @saved="handleMonitorDateSaved"
            />
          </transition>
        </div>
      </div>
    </transition>
  </AppLayout>
</template>

<script>
import AppLayout from '../../components/app/AppLayout.vue';
import SearchBar from '../../components/common/SearchBar.vue';
import TRTBoardColumn from '../../components/trt/TRTBoardColumn.vue';
import AddTRTPatientModal from '../../modals/AddTRTPatientModal.vue';
import ConnectEmrModal from '../../modals/ConnectEmrModal.vue';
import SyncTRTModal from '../../modals/SyncTRTModal.vue';
import SetMonitorDateModal from '../../modals/SetMonitorDateModal.vue';
import RefreshIcon from 'vue-material-design-icons/Refresh.vue';
import AlertIcon from 'vue-material-design-icons/AlertOutline.vue';
import Tooltip from '@/components/common/Tooltip.vue';
import * as api from '@/api';
import { queryClient } from '@/libs/queryClient';
import { alertService } from '@/libs/alert';
import { isExtensionMode } from '@/libs/extensionAuth';
import { COLUMNS, getBoardColumn } from '@/utils/trt';

const REFRESH_DISABLED_TEXT = 'Launch CRM from Practice Fusion to refresh TRT data (recommended daily).';

export default {
  name: 'TRTDashboardView',
  components: { AppLayout, SearchBar, TRTBoardColumn, AddTRTPatientModal, ConnectEmrModal, SyncTRTModal, SetMonitorDateModal, RefreshIcon, AlertIcon, Tooltip },
  data() {
    return {
      REFRESH_DISABLED_TEXT,
      searchTerm: '',
      patients: [],
      isLoading: false,
      pendingPatient: null,
      monitorPatient: null,
      modal: {
        open: false,
        id: ''
      }
    };
  },
  computed: {
    // Syncing talks to the EMR through the extension bridge, which only exists
    // when the app is loaded inside the Practice Fusion modal.
    canRefresh() {
      return isExtensionMode();
    },
    searchedPatients() {
      const term = this.searchTerm.trim().toLowerCase();
      if (!term) return this.patients;
      return this.patients.filter((p) => {
        const name = `${p.firstName || ''} ${p.lastName || ''}`.toLowerCase();
        return name.includes(term);
      });
    },
    boardColumns() {
      return COLUMNS.map((col) => ({
        ...col,
        records: this.searchedPatients.filter((p) => getBoardColumn(p) === col.key)
      }));
    }
  },
  created() {
    this.loadPatients();
  },
  methods: {
    async loadPatients() {
      try {
        this.isLoading = true;
        const cards = await queryClient.fetchQuery({
          queryKey: ['trt-cards'],
          queryFn: () => api.fetchTrtCards()
        });
        this.patients = cards;
      } catch (err) {
        console.error('Failed to load TRT patients:', err);
        alertService.error('Failed to load TRT patients: ' + err.message);
        this.patients = [];
      } finally {
        this.isLoading = false;
      }
    },
    handleRefresh() {
      this.openModal('syncTrt');
    },
    openMonitorDateModal(record) {
      this.monitorPatient = record;
      this.openModal('monitorDate');
    },
    async handleMonitorDateSaved() {
      this.closeModal();
      await this.loadPatients();
    },
    // The sync modal already invalidated the cache; reload so the board reflects
    // the freshly-synced data once the user closes it.
    async handleSyncDone() {
      await this.loadPatients();
    },
    handleSearchChange(value) {
      this.searchTerm = value;
    },
    // Modal management (mirrors the pattern used across list views)
    openAddPatientModal() {
      this.openModal('addPatient');
    },
    openModal(id) {
      this.modal.open = true;
      setTimeout(() => {
        this.modal.id = id;
      });
    },
    closeModal() {
      this.modal.id = '';
      this.pendingPatient = null;
      this.monitorPatient = null;
      setTimeout(() => {
        this.modal.open = false;
      });
    },
    handleModalClick(event) {
      if (event.target.classList.contains('close-on-click')) {
        this.closeModal();
      }
    },
    // Patient already has an EMR link — enroll directly.
    handleAddPatient(patient) {
      this.enrollPatient(patient.patientId);
    },
    // Patient has no EMR link yet — move to the EMR search step.
    handleContinueToEmr(patient) {
      this.pendingPatient = patient;
      this.modal.id = 'connectEmr';
    },
    // EMR record chosen — persist its integration id onto the patient, then enroll.
    async handleEmrConnected(emrPatient) {
      const patient = this.pendingPatient;
      if (!patient) return;
      try {
        const payload = this.buildPatientPayload({
          ...patient,
          ehrId: emrPatient.emrIntegrationId
        });
        await api.updatePatient(patient.patientId, payload);

        queryClient.invalidateQueries({ queryKey: ['patients'] });
        queryClient.invalidateQueries({ queryKey: ['patient', patient.patientId] });

        this.enrollPatient(patient.patientId);
      } catch (err) {
        console.error('Failed to connect EMR patient:', err);
        alertService.error('Failed to connect patient: ' + err.message);
      }
    },
    async enrollPatient(patientId) {
      try {
        await api.enrollTrtPatient(patientId);
        queryClient.invalidateQueries({ queryKey: ['trt-cards'] });
        // Enrollment flips isTrtPatient, so drop the patient list/search cache
        // to reflect the new TRT status
        queryClient.invalidateQueries({ queryKey: ['patients'] });
        this.closeModal();
        await this.loadPatients();
      } catch (err) {
        console.error('Failed to enroll TRT patient:', err);
        alertService.error('Failed to add patient to TRT: ' + err.message);
      }
    },
    buildPatientPayload(patient) {
      return {
        firstName: patient.firstName,
        lastName: patient.lastName,
        dateOfBirth: patient.dateOfBirth || null,
        marketingSource: patient.marketingSource || null,
        email: patient.email || null,
        mobilePhone: patient.mobilePhone || null,
        homePhone: patient.homePhone || null,
        workPhone: patient.workPhone || null,
        addressStreet: patient.addressStreet || null,
        addressCity: patient.addressCity || null,
        addressState: patient.addressState || null,
        addressZip: patient.addressZip || null,
        notes: patient.notes || null,
        ehrId: patient.ehrId || null
      };
    }
  }
};
</script>

<style scoped>
.header-left {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
}

.header-left > :first-child {
  width: 260px;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.layout-header .button {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
}

.button-icon {
  display: inline-flex;
}

/* Disabled buttons swallow mouse events in Chrome, which would stop the
   Tooltip wrapper from ever seeing a mouseenter. Let hovers pass through to
   the wrapper instead — the button is inert either way. */
.refresh-blocked {
  pointer-events: none;
}

.warning-icon {
  color: #b8860b;
}

.spinning {
  animation: trt-spin 0.8s linear infinite;
}

@keyframes trt-spin {
  to {
    transform: rotate(360deg);
  }
}

/* Let the whole view scroll (both directions) instead of each column
   scrolling internally, so cards keep their natural height. */
.layout-body {
  overflow: auto;
}

/* Board */
.trt-board {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  min-height: 100%;
  padding: 16px;
  width: max-content;
}
</style>
